import { IntlShape } from "react-intl";
import {
  AiImportStatus,
  AiImportStatusResponse,
  TendersWithAiImportsQuery
} from "@/graphql/__generated__/graphql";
import { formatDateLocale } from "@/utils/helpers";
import {
  computeLastIndex,
  computeProgressFromSteps,
  buildAiImportDisplaySteps,
  getPublicationNameForId,
} from "@/utils/aiImport";
import { ChunkMessage } from "@/constants/enums";
import { AiImportResponseTypes } from "@/constants/enums";
import { NavigateFunction } from "react-router-dom";
import { Dispatch, SetStateAction } from "react";

/**
 * Interface for AI import step structure
 */
export interface Step {
  id: number;
  node: string;
  type: AiImportResponseTypes.PROGRESS;
  title: string;
  content: string;
}

/**
 * Interface for grouped import history
 */
export interface GroupedHistoryItem {
  key: string;
  label: string;
  items: AiImportStatusResponse[];
}

/**
 * Interface for import calculations result
 */
export interface ImportCalculations {
  calculations: Record<number, {
    lastIndex: number;
    progress: number;
    displaySteps: Step[];
    publicationName: string | undefined;
  }>;
  averageProgress: number;
  totalImports: number;
}

/**
 * Extracts AI import IDs that are already attached to tenders from the query result
 *
 * @param tendersWithAiImports - The query result containing tenders with AI imports
 * @returns Set of AI import IDs that are already in use
 */
export const extractUsedAiImportIds = (
  tendersWithAiImports: TendersWithAiImportsQuery | undefined
): Set<number> => {
  const ids: number[] = [];
  const edges = tendersWithAiImports?.paginatedTenders?.edges ?? [];

  for (const edge of edges) {
    const docs = edge.node?.documents ?? [];
    for (const doc of docs) {
      if (doc?.aiImportId != null) {
        ids.push(doc.aiImportId);
      }
    }
  }

  return new Set(ids);
};

/**
 * Filters and sorts completed AI imports that are not already used in tenders
 *
 * @param aiImportStatuses - Array of all AI import statuses
 * @param usedAiImportIds - Set of AI import IDs that are already in use
 * @returns Array of completed imports sorted by creation date (newest first)
 */
export const getCompletedImportHistory = (
  aiImportStatuses: AiImportStatusResponse[],
  usedAiImportIds: Set<number>
): AiImportStatusResponse[] => {
  return aiImportStatuses
    .filter((ai) =>
      (ai.status === AiImportStatus.Success || ai.status === AiImportStatus.Failed) &&
      !usedAiImportIds.has(ai.id)
    )
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

/**
 * Groups completed imports by local day for history display
 *
 * @param completedHistory - Array of completed imports
 * @param intl - React Intl instance for date formatting
 * @returns Array of grouped history items by day
 */
export const groupImportHistoryByDay = (
  completedHistory: AiImportStatusResponse[],
  intl: IntlShape
): GroupedHistoryItem[] => {
  const map = new Map<string, { label: string; items: AiImportStatusResponse[] }>();

  for (const item of completedHistory) {
    const d = new Date(item.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = formatDateLocale(intl, d);
    const existing = map.get(key);

    if (existing) {
      existing.items.push(item);
    } else {
      map.set(key, { label, items: [item] });
    }
  }

  // Return groups sorted by day desc
  return Array.from(map.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([key, group]) => ({ key, ...group }));
};

/**
 * Computes calculations for all active imports including progress and display steps
 *
 * @param activeImports - Record of active imports with their steps
 * @param aiImportStatuses - Array of all AI import statuses
 * @param getLocalizedStepContent - Function to get localized step content
 * @returns Object containing calculations, average progress, and total imports
 */
export const computeImportCalculations = (
  activeImports: Record<number, { steps: any[] }>,
  aiImportStatuses: AiImportStatusResponse[],
  getLocalizedStepContent: (_stepIndex: number, _messageType: ChunkMessage) => string
): ImportCalculations => {
  const calculations: Record<number, {
    lastIndex: number;
    progress: number;
    displaySteps: Step[];
    publicationName: string | undefined;
  }> = {};

  let totalProgress = 0;
  let totalImports = 0;

  Object.keys(activeImports).forEach((idStr) => {
    const aiImportId = Number(idStr);
    const steps = activeImports[aiImportId]?.steps ?? [];
    const lastIndex = computeLastIndex(steps);
    const progress = computeProgressFromSteps(steps);
    const displaySteps = buildAiImportDisplaySteps(getLocalizedStepContent, lastIndex, progress);
    const publicationName = getPublicationNameForId(aiImportStatuses, aiImportId);

    calculations[aiImportId] = {
      lastIndex,
      progress,
      displaySteps,
      publicationName,
    };

    totalProgress += progress;
    totalImports++;
  });

  const averageProgress = totalImports > 0 ? Math.round(totalProgress / totalImports) : 0;

  return { calculations, averageProgress, totalImports };
};

/**
 * Checks if an import should be ended based on its status
 *
 * @param aiImportStatuses - Array of all AI import statuses
 * @param activeImportIds - Array of currently active import IDs
 * @returns Array of import IDs that should be ended
 */
export const getImportsToEnd = (
  aiImportStatuses: AiImportStatusResponse[],
  activeImportIds: number[]
): number[] => {
  const importsToEnd: number[] = [];

  for (const id of activeImportIds) {
    const found = aiImportStatuses.find((s) => s.id === id);
    if (!found || found.status === AiImportStatus.Success || found.status === AiImportStatus.Failed) {
      importsToEnd.push(id);
    }
  }

  return importsToEnd;
};

/**
 * Creates a navigation handler for viewing completed imports
 *
 * @param navigate - React Router navigate function
 * @param setOpen - Function to close the dialog
 * @returns Handler function that takes an import ID and navigates to the create tender page
 */
export const createViewClickHandler = (
  navigate: NavigateFunction,
  setOpen: Dispatch<SetStateAction<boolean>>
) => {
  return (id: number) => {
    navigate(`/tender/new/?importID=${id}`);
    setOpen(false);
  };
};

/**
 * Creates a localized step content getter function
 *
 * @param t - Translation function
 * @param stepKeys - Array of step keys for localization
 * @returns Function that gets localized content for a specific step and message type
 */
export const createLocalizedStepContentGetter = (
  t: (_key: string) => string,
  stepKeys: readonly string[]
) => {
  return (stepIndex: number, messageType: ChunkMessage): string => {
    const stepKey = stepKeys[stepIndex];
    if (!stepKey) return "";
    return t(`aiimportloader.steps.${stepKey}.${messageType}`);
  };
};
